import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { WorkingDayEmployee } from '@models/working-day-employee';
import { EmployeeService } from '@services/employee/employee.service';
import { PlatformCheckerService } from '@services/platform-checker/platform-checker.service';
import { YearMonth } from '../../models/year-month';
import { LogsService } from '../../services/logs.service';

@Component({
  selector: 'app-employee-logs',
  standalone: false,
  templateUrl: './employee-logs.component.html',
  styleUrls: ['./employee-logs.component.scss'],
})
export class EmployeeLogsComponent implements OnInit, OnDestroy {
  private employeeSubscription: Subscription;
  private routeSubscription: Subscription;

  public isLoading = false;
  public months: YearMonth[] = [];
  public userName: string;
  public employeeId = '';
  public currentBalance: number;
  public balancePreviousYears: number;
  public lockedBalance: number;
  public isMobile = false;
  public showHideBalance = false;
  public friendsAndFamily = false;
  public loggedUserHasRemoteWork: boolean;
  public remoteWorkPercentageHistorical = 0;
  public showSampleHistoricalPercentage = false;

  public constructor(
    public employeeService: EmployeeService,
    public platformCheckerService: PlatformCheckerService,
    private readonly logsService: LogsService,
    private readonly cdr: ChangeDetectorRef,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  public ionViewDidEnter(): void {
    this.loadData();
  }

  private loadData(): void {
    this.employeeSubscription?.unsubscribe();
    this.isLoading = true;
    this.isMobile = this.platformCheckerService.isMobile();
    this.employeeSubscription = this.employeeService.employee$
      .pipe(
        concatMap((employee: WorkingDayEmployee): Observable<YearMonth[]> => {
          this.userName = employee.firstName;
          this.employeeId = employee.id;
          this.currentBalance = employee.currentBalance;
          this.balancePreviousYears = employee.balancePreviousYears;
          this.lockedBalance = employee.lockedBalance;
          this.showHideBalance = employee.shouldHideBalance;
          this.loggedUserHasRemoteWork = employee.hasRemoteWork;
          this.friendsAndFamily = employee.friendsAndFamily;
          this.remoteWorkPercentageHistorical =
            employee.remoteWorkPercentageHistorical ?? 0;
          this.showSampleHistoricalPercentage =
            employee.showSampleHistoricalPercentage ?? false;

          return this.logsService.getAvailableYearMonths(employee.id);
        })
      )
      .subscribe((months: YearMonth[]): void => {
        this.isLoading = false;
        this.months = months;
        this.cdr.markForCheck();
      });
  }

  public ngOnInit(): void {
    this.routeSubscription = this.route.queryParams.subscribe(params => {
      this.loadData();
      if (Object.keys(params).length > 0) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true,
        });
      }
    });
  }

  public ngOnDestroy(): void {
    this.ionViewDidLeave();
    this.routeSubscription?.unsubscribe();
  }

  public ionViewDidLeave(): void {
    this.employeeSubscription?.unsubscribe();
  }

  public shouldShowCurrentBalanceCard(): boolean {
    return this.currentBalance !== undefined && !this.showHideBalance;
  }

  public shouldShowRemoteWorkHistoricalPercentage(): boolean {
    return !!(
      this.loggedUserHasRemoteWork && this.showSampleHistoricalPercentage
    );
  }
}
